/**
 * @file Maintenance detail-view fields, controls, and save behavior.
 */

import { appData, state } from "../../app/state/state.js";
import { canAccessView } from "../../app/routing/boardAccess.js";
import {
  formatCurrency,
} from "../../shared/utils/displayUtils.js";
import {
  sanitizeHTML,
} from "../../shared/utils/contentUtils.js";
import {
  renderAuditTrail,
  renderLongTextDetailSection,
} from "../../shared/details/detailSections.js";
import {
  clearDetailDirtyField,
  clearDetailDirtyFields,
  hasDetailDirtyFields,
  isDetailFieldDirty,
  recordDetailFieldBaseline,
} from "../../shared/details/detailDirtyState.js";
import {
  getLongTextContainer,
  setLongTextContainerValue,
} from "../../shared/details/detailLongText.js";
import {
  createDateControl,
} from "../../shared/ui/controls/dateControl.js";
import {
  focusFirstRegisteredControlError,
  mergeRegisteredControlValues,
  updateRegisteredControl,
  updateControlValue,
  validateRegisteredControls,
} from "../../shared/ui/controls/controlCore.js";
import {
  applyParentCascadingClosure,
  applyParentCascadingHold,
  getParentClosePreflight,
  getParentHoldPreflight,
} from "../../shared/api/parentClosePreflight.js";
import {
  LINKED_PARENT_ACTIONS,
} from "../../shared/details/linkedParentSection.js";
import {
  openParentClosePreflightModal,
} from "../../shared/details/parentClosePreflight.js";
import {
  createPrioritySelectPreset,
  createStreamScopedStatusSelectPreset,
  createStreamScopedTypeSelectPreset,
  createUserSelectPreset,
} from "../../shared/ui/controls/controlPresets.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import {
  DETAIL_TITLE_INPUT_CLASSES,
  NATIVE_INPUT_CLASSES,
  getNativeFieldWrapperHtml,
  validateNativeFormFields,
} from "../../shared/forms/nativeFieldStyles.js";
import {
  saveMaintenanceItem,
  updateMaintenanceAssetLink,
} from "./maintenanceApi.js";
import {
  attachMaintenanceAssetLinkFieldListeners,
  getMaintenanceAssetDisplay,
  openMaintenanceAssetLookupModal,
} from "./maintenanceAssetLookupModal.js";
import {
  getMaintenanceCloseFailureMessage,
  openMaintenanceCloseModal,
  shouldOpenMaintenanceCloseModal,
} from "./maintenanceCloseModal.js";
import {
  mountMaintenanceTechnicianControl,
  renderMaintenanceTechnicianControlRoot,
} from "./maintenanceTechnicianControl.js";

const LIVE_SAVE_FIELDS = Object.freeze([
  "priority",
  "status",
  "types",
]);

function attrValue(value = "") {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function getMaintenanceStream(item = {}) {
  return item?.stream === "issue" ? "issue" : "service";
}

function getStreamLabel(stream) {
  return stream === "issue" ? "Issue" : "Service";
}

function getStreamIcon(stream) {
  return stream === "issue" ? "report" : "build_circle";
}

function getStreamOutlineColor(stream) {
  return stream === "issue" ? "#d65f5f" : "#4599e8";
}

function getStreamFillColor(stream) {
  return stream === "issue" ? "#fde3e3" : "#e4f2ff";
}

function renderMaintenanceStreamIcon(stream, className = "") {
  const streamLabel = getStreamLabel(stream);
  const streamIcon = getStreamIcon(stream);

  return `
    <span
      class="maintenance-stream-icon-colored shrink-0 leading-none ${className}"
      style="--maintenance-stream-outline: ${getStreamOutlineColor(stream)}; --maintenance-stream-fill: ${getStreamFillColor(stream)};"
      title="${attrValue(streamLabel)}"
      aria-label="${attrValue(streamLabel)}"
    >
      <span class="maintenance-stream-icon-colored__fill material-symbols-outlined" aria-hidden="true">${streamIcon}</span>
      <span class="maintenance-stream-icon-colored__outline material-symbols-outlined">${streamIcon}</span>
    </span>
  `;
}

function scalarControlRootHtml(fieldName) {
  return `<div class="min-w-0" data-maintenance-detail-control="${fieldName}"></div>`;
}

function inputHtml(type, name, value = "", options = {}) {
  const className = [NATIVE_INPUT_CLASSES, options.className || ""]
    .filter(Boolean)
    .join(" ");
  const placeholderAttr = options.placeholder ?
    ` placeholder="${attrValue(options.placeholder)}"` :
    "";
  const extraAttrs = options.extraAttrs ? ` ${options.extraAttrs}` : "";
  return `<input type="${type}" name="${name}" value="${attrValue(value)}" class="${className}"${placeholderAttr}${extraAttrs}>`;
}

function userCanOpenAssets() {
  return canAccessView(state.currentUserProfile, "assets");
}

function renderItemTitle(item) {
  return `
    <textarea
      name="item"
      class="${DETAIL_TITLE_INPUT_CLASSES} resize-none overflow-hidden"
      placeholder="Maintenance Item"
      aria-label="Maintenance Item"
      autocomplete="off"
      rows="1"
      data-auto-grow="true"
      required
    >${attrValue(item?.item || "")}</textarea>
  `;
}

function renderDetailHeaderAction({
  action,
  icon,
  label,
  targetId = "",
  variant = "default",
}) {
  const classes = variant === "danger" ?
    "inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-red-600 focus:outline-none focus:text-red-600" :
    "inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-[var(--dfc-brand-primary)] focus:outline-none focus:text-[var(--dfc-brand-primary)]";
  return `
    <button
      type="button"
      class="${classes}"
      data-linked-parent-action="${attrValue(action)}"
      data-linked-parent-field="maintenance-linked-asset"
      data-linked-parent-id="${attrValue(targetId)}"
      data-linked-parent-type="asset"
      title="${attrValue(label)}"
      aria-label="${attrValue(label)}"
    >
      <span class="material-symbols-outlined pointer-events-none text-base leading-none">${icon}</span>
    </button>
  `;
}

function renderAssetHeaderContext(item) {
  const display = getMaintenanceAssetDisplay(item);
  const hasLink = Boolean(display.id);
  if (!hasLink) {return "";}

  const canOpen = hasLink && userCanOpenAssets();
  const label = display.name || "Linked equipment";
  const textClass = canOpen ?
    "task-parent-header-link inline cursor-pointer border-0 bg-transparent p-0 text-left text-[15px] font-medium text-gray-400 transition-colors hover:text-[var(--dfc-brand-primary)] focus:outline-none focus:text-[var(--dfc-brand-primary)]" :
    "inline-block text-[15px] font-medium text-gray-400";
  const contextText = canOpen ? `
    <button
      type="button"
      class="${textClass}"
      data-linked-parent-action="${LINKED_PARENT_ACTIONS.OPEN}"
      data-linked-parent-field="maintenance-linked-asset"
      data-linked-parent-id="${attrValue(display.id)}"
      data-linked-parent-type="asset"
    >Equipment: ${sanitizeHTML(label)}</button>
  ` : `
    <span class="${textClass}">Equipment: ${sanitizeHTML(label)}</span>
  `;

  return `
    <div class="mb-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1" data-linked-parent-field="maintenance-linked-asset">
      <input type="hidden" name="linked_asset_id" value="${attrValue(display.id)}">
      <input type="hidden" name="linked_asset_name" value="${attrValue(hasLink ? label : "")}">
      ${contextText}
      <span class="inline-flex items-center gap-0.5">
        ${renderDetailHeaderAction({
          action: LINKED_PARENT_ACTIONS.LINK,
          icon: hasLink ? "sync_alt" : "link",
          label: hasLink ? "Change linked equipment" : "Link equipment",
          targetId: display.id,
        })}
        ${hasLink ? renderDetailHeaderAction({
          action: LINKED_PARENT_ACTIONS.UNLINK,
          icon: "link_off",
          label: "Unlink equipment",
          targetId: display.id,
          variant: "danger",
        }) : ""}
      </span>
    </div>
  `;
}

function renderAssetLinkButtonField(item) {
  if (item?.linked_asset_id) {
    return '<div class="hidden md:block" aria-hidden="true"></div>';
  }

  return getNativeFieldWrapperHtml("Link", `
    <button
      type="button"
      class="flex min-h-10 w-full items-center rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-left text-base font-medium text-gray-500 transition-colors hover:border-gray-300 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)] sm:min-h-[30px] sm:text-sm"
      data-linked-parent-action="${LINKED_PARENT_ACTIONS.LINK}"
      data-linked-parent-field="maintenance-linked-asset"
      data-linked-parent-id=""
      data-linked-parent-type="asset"
      aria-label="Link to an equipment item"
    >
      Link to an equipment item
    </button>
  `);
}

function renderIssueFields(item) {
  return `
    ${getNativeFieldWrapperHtml("Urgency", scalarControlRootHtml("priority"))}
    ${getNativeFieldWrapperHtml("Status", scalarControlRootHtml("status"))}
    ${getNativeFieldWrapperHtml("Identified By", scalarControlRootHtml("identified_by"))}
    ${getNativeFieldWrapperHtml("Identified On", scalarControlRootHtml("identified_on"))}
    ${renderLongTextDetailSection({
      fieldName: "details",
      label: "Issue Details",
      value: item?.details || "",
      wrapperClass: "md:col-span-4",
    })}
    <div class="md:col-span-4 pt-1">
      <h3 class="text-[15px] font-semibold text-gray-500 dark:text-gray-400">Repair</h3>
      <div class="mt-2 h-px w-full bg-gray-200 dark:bg-gray-700"></div>
    </div>
    ${getNativeFieldWrapperHtml("Due Date", scalarControlRootHtml("due_date"))}
    ${getNativeFieldWrapperHtml("Completed On", scalarControlRootHtml("completed_on"))}
    ${getNativeFieldWrapperHtml("Technician", `
      ${renderMaintenanceTechnicianControlRoot()}
    `)}
    ${getNativeFieldWrapperHtml("Cost", inputHtml("text", "cost", formatCurrency(item?.cost) || "", {
      className: `${NATIVE_INPUT_CLASSES} currency-input`,
      placeholder: "$",
      extraAttrs: 'inputmode="decimal"',
    }))}
    ${renderLongTextDetailSection({
      fieldName: "repair_details",
      label: "Repair Details",
      value: item?.repair_details || "",
      wrapperClass: "md:col-span-4",
    })}
  `;
}

/**
 * Generates HTML for editable Maintenance detail fields.
 * @param {object} item Maintenance item data.
 * @returns {string} Maintenance detail fields HTML.
 */
export function getMaintenanceDetailFields(item = {}) {
  const stream = getMaintenanceStream(item);

  return `
    <div class="bg-white pt-6 px-6 pb-3 rounded-lg shadow-md mb-6">
      <input type="hidden" name="stream" value="${attrValue(stream)}">
      <div class="mb-4 pb-2">
        ${renderAssetHeaderContext(item)}
        <div class="flex min-w-0 items-start justify-between gap-3">
          <div class="min-w-0 flex-1">${renderItemTitle(item)}</div>
          ${renderMaintenanceStreamIcon(stream, "mt-0.5 text-[28px] sm:text-[36px]")}
        </div>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-6 text-sm">
        ${getNativeFieldWrapperHtml("Type(s)", scalarControlRootHtml("types"), {className: "md:col-span-2"})}
        ${getNativeFieldWrapperHtml("Managed By", scalarControlRootHtml("managed_by"))}
        ${renderAssetLinkButtonField(item)}

        ${stream === "issue" ? renderIssueFields(item) : `
          ${getNativeFieldWrapperHtml("Urgency", scalarControlRootHtml("priority"))}
          ${getNativeFieldWrapperHtml("Status", scalarControlRootHtml("status"))}
          ${getNativeFieldWrapperHtml("Due Date", scalarControlRootHtml("due_date"))}
          <div class="hidden md:block" aria-hidden="true"></div>
          ${getNativeFieldWrapperHtml("Completed On", scalarControlRootHtml("completed_on"))}
          ${getNativeFieldWrapperHtml("Technician", `
            ${renderMaintenanceTechnicianControlRoot()}
          `)}
          ${getNativeFieldWrapperHtml("Cost", inputHtml("text", "cost", formatCurrency(item?.cost) || "", {
            className: `${NATIVE_INPUT_CLASSES} currency-input`,
            placeholder: "$",
            extraAttrs: 'inputmode="decimal"',
          }))}
          ${renderLongTextDetailSection({
            fieldName: "details",
            label: "Details",
            value: item?.details || "",
            wrapperClass: "md:col-span-4",
          })}
        `}
      </div>
      ${renderAuditTrail(item)}
    </div>
  `;
}

function getControlRoot(form, fieldName) {
  return form?.querySelector(`[data-maintenance-detail-control="${fieldName}"]`);
}

/**
 * Mounts registered controls for Maintenance details.
 * @param {HTMLElement} detailsViewContainer Active detail view container.
 * @param {object} item Maintenance item data.
 * @returns {object} Mounted controls keyed by field name.
 */
export function setupMaintenanceDetailControls(detailsViewContainer, item = {}) {
  const controls = {};
  const form = detailsViewContainer?.querySelector("#details-form");
  if (!form) {return controls;}

  const stream = getMaintenanceStream(item);

  const typeRoot = getControlRoot(form, "types");
  if (typeRoot) {
    controls.types = createStreamScopedTypeSelectPreset({
      root: typeRoot,
      appData,
      itemType: "maintenance",
      stream,
      value: Array.isArray(item?.types) ? item.types : [],
      searchable: true,
    });
  }

  const managedByRoot = getControlRoot(form, "managed_by");
  if (managedByRoot) {
    controls.managed_by = createUserSelectPreset({
      root: managedByRoot,
      appData,
      name: "managed_by",
      collectionName: "maintenance",
      value: item?.managed_by || "",
      clearable: false,
      searchable: true,
    });
  }

  const priorityRoot = getControlRoot(form, "priority");
  if (priorityRoot) {
    controls.priority = createPrioritySelectPreset({
      root: priorityRoot,
      appData,
      value: item?.priority || "",
      clearable: false,
    });
  }

  const statusRoot = getControlRoot(form, "status");
  if (statusRoot) {
    controls.status = createStreamScopedStatusSelectPreset({
      root: statusRoot,
      appData,
      itemType: "maintenance",
      stream,
      value: item?.status || "",
      clearable: false,
    });
  }

  ["due_date", "completed_on", "identified_on"].forEach((fieldName) => {
    const root = getControlRoot(form, fieldName);
    if (!root) {return;}
    controls[fieldName] = createDateControl({
      root,
      name: fieldName,
      type: "date",
      value: item?.[fieldName] || "",
    });
  });

  const identifiedByRoot = getControlRoot(form, "identified_by");
  if (identifiedByRoot) {
    controls.identified_by = createUserSelectPreset({
      root: identifiedByRoot,
      appData,
      name: "identified_by",
      collectionName: "maintenance",
      value: item?.identified_by || "",
      clearable: false,
      searchable: true,
    });
  }

  controls.technician = mountMaintenanceTechnicianControl({
    root: form.querySelector("[data-maintenance-technician-control]"),
    appData,
    value: item?.technician || "",
  });

  return controls;
}

function normalizeCurrencyInput(value) {
  if (value === null || value === undefined || value === "") {return null;}
  const number = typeof value === "number" ?
    value :
    Number(String(value).replace(/[$,\s]/g, ""));
  return Number.isFinite(number) ? number : null;
}

function isCloseInterceptStatus(statusId, stream) {
  const closeStatusId =
    appData?.config?.maintenance?.closeInterceptStatusByStream?.[stream] || "";
  return Boolean(closeStatusId && statusId === closeStatusId);
}

function isMaintenanceOnHoldStatus(statusId) {
  return (appData?.config?.maintenanceStatus || [])
    .some((status) => status?.id === statusId && status?.isOnHold === true);
}

function getSelectedMaintenanceTypes(item = {}) {
  const selectedTypeIds = new Set(
    (Array.isArray(item?.types) ? item.types : [])
      .map((typeId) => String(typeId || ""))
      .filter(Boolean),
  );
  if (selectedTypeIds.size === 0) {return [];}
  return (Array.isArray(appData?.config?.maintenanceTypes) ?
    appData.config.maintenanceTypes :
    []).filter((typeConfig) => selectedTypeIds.has(String(typeConfig?.id || "")));
}

function getRequiredRepairDynamicFields(item = {}) {
  if (item?.stream !== "issue") {return [];}
  return getSelectedMaintenanceTypes(item)
    .flatMap((typeConfig) => Array.isArray(typeConfig?.repairDynamicFields) ?
      typeConfig.repairDynamicFields :
      [])
    .filter((field) => field?.required === true && field?.type !== "heading");
}

function repairDetailsIncludeField(repairDetails, field = {}) {
  const label = String(field?.label || "").trim();
  if (!label) {return true;}
  const details = String(repairDetails || "");
  return details.includes(`**${label}**:`) ||
    details.toLowerCase().includes(label.toLowerCase());
}

function getCompletionBlockers(candidate) {
  if (!isCloseInterceptStatus(candidate.status, candidate.stream)) {
    return [];
  }

  const blockers = [];
  if (!String(candidate.technician || "").trim()) {
    blockers.push("Technician is required before this closed Maintenance status can be saved.");
  }
  if (normalizeCurrencyInput(candidate.cost) === null) {
    blockers.push("Cost is required before this closed Maintenance status can be saved.");
  }
  if (!String(candidate.completed_on || "").trim()) {
    blockers.push("Completed On is required before this closed Maintenance status can be saved.");
  }
  if (candidate.stream === "issue") {
    getRequiredRepairDynamicFields(candidate)
      .filter((field) => !repairDetailsIncludeField(candidate.repair_details, field))
      .forEach((field) => {
        blockers.push(
          `${field?.label || "Repair field"} is required before this closed Maintenance status can be saved.`,
        );
      });
  }
  return blockers;
}

function setNativeRequired(input, required) {
  if (!input) {return;}
  input.required = Boolean(required);
  if (!required) {
    input.setCustomValidity("");
  }
}

function setRepairDetailsInlineError(detailsForm, message = "") {
  const root = detailsForm?.closest?.("#details-view") || detailsForm || document;
  const container = getLongTextContainer(root, "repair_details");
  if (!container) {return;}

  const editor = container.querySelector(".long-text-editor");
  if (editor) {
    editor.setCustomValidity(message);
  }

  let messageElement = container.querySelector("[data-maintenance-repair-validation]");
  if (!messageElement) {
    messageElement = document.createElement("p");
    messageElement.dataset.maintenanceRepairValidation = "true";
    messageElement.className = "mt-1 hidden text-xs font-medium text-red-600";
    messageElement.setAttribute("aria-live", "polite");
    container.append(messageElement);
  }
  messageElement.textContent = message;
  messageElement.classList.toggle("hidden", !message);

  const display = container.querySelector(".long-text-display");
  if (display) {
    display.setAttribute("aria-invalid", String(Boolean(message)));
    display.classList.toggle("border-red-500", Boolean(message));
    display.classList.toggle("shadow-[0_0_0_1.5px_rgba(220,38,38,0.22)]", Boolean(message));
  }
}

function focusRepairDetailsValidation(detailsForm) {
  const root = detailsForm?.closest?.("#details-view") || detailsForm || document;
  const container = getLongTextContainer(root, "repair_details");
  const target = container?.querySelector(".long-text-editor, .long-text-display");
  target?.focus?.({preventScroll: true});
  target?.scrollIntoView?.({block: "center", behavior: "smooth"});
}

function applyCompletionRequiredState(detailsForm, candidate) {
  const isClosed = isCloseInterceptStatus(candidate.status, candidate.stream);
  const technicianRoot = detailsForm?.querySelector?.("[data-maintenance-technician-control]");
  const completedOnRoot = detailsForm?.querySelector?.(
    '[data-maintenance-detail-control="completed_on"]',
  );
  const costInput = detailsForm?.querySelector?.('input[name="cost"]');

  if (technicianRoot?.dataset?.controlId) {
    updateRegisteredControl(technicianRoot.dataset.controlId, {required: isClosed});
  }
  if (completedOnRoot?.dataset?.controlId) {
    updateRegisteredControl(completedOnRoot.dataset.controlId, {required: isClosed});
  }
  setNativeRequired(costInput, isClosed);
  setRepairDetailsInlineError(detailsForm, "");
}

function getMissingRepairDynamicFieldLabels(candidate) {
  if (candidate.stream !== "issue") {return [];}
  return getRequiredRepairDynamicFields(candidate)
    .filter((field) => !repairDetailsIncludeField(candidate.repair_details, field))
    .map((field) => field?.label || "Repair field");
}

function showMaintenanceSaveBlockers(blockers = []) {
  const listHtml = blockers.length > 0 ?
    `<ul class="list-disc pl-5 text-left">${blockers.map((message) =>
      `<li>${sanitizeHTML(message)}</li>`,
    ).join("")}</ul>` :
    "Complete the required Maintenance fields before saving.";
  openConfirmationModal(
    "Maintenance Completion Required",
    listHtml,
    async () => {},
    false,
    "OK",
  );
}

function getFormPayload(form) {
  const formData = new FormData(form);
  const payload = {};
  for (const [key, value] of formData.entries()) {
    if (
      key === "linked_asset_id" ||
      key === "linked_asset_name" ||
      key === "undefined" ||
      key.includes("form-")
    ) {
      continue;
    }
    payload[key] = typeof value === "string" ? value : value;
  }
  const merged = mergeRegisteredControlValues(payload, form);
  if (Object.prototype.hasOwnProperty.call(merged, "cost")) {
    merged.cost = normalizeCurrencyInput(merged.cost);
  }
  return merged;
}

function getCompletionCandidate(item, payload) {
  return {
    ...item,
    ...payload,
    cost: Object.prototype.hasOwnProperty.call(payload, "cost") ?
      payload.cost :
      item?.cost,
    stream: item?.stream || payload.stream || "service",
  };
}

function getCompletionFieldNames(stream) {
  return stream === "issue" ?
    ["technician", "cost", "completed_on", "repair_details"] :
    ["technician", "cost", "completed_on"];
}

function getCompletionFieldLabel(fieldName) {
  if (fieldName === "technician") {return "Technician";}
  if (fieldName === "cost") {return "Cost";}
  if (fieldName === "completed_on") {return "Completed On";}
  if (fieldName === "repair_details") {return "Repair Details";}
  return fieldName;
}

function getDirtyCompletionFields(detailsForm, stream) {
  if (!detailsForm) {return [];}
  return getCompletionFieldNames(stream)
    .filter((fieldName) => isDetailFieldDirty(detailsForm, fieldName));
}

function setRegisteredDetailControlValue(detailsForm, selector, value) {
  const root = detailsForm?.querySelector?.(selector);
  const controlId = root?.dataset?.controlId || "";
  if (!controlId) {return;}
  updateControlValue(controlId, value || "", {silent: true});
}

function syncCompletionFieldDraftState({
  detailsForm,
  item,
}) {
  if (!detailsForm) {return;}
  const stream = getMaintenanceStream(item);
  setRegisteredDetailControlValue(
    detailsForm,
    "[data-maintenance-technician-control]",
    item?.technician || "",
  );
  setRegisteredDetailControlValue(
    detailsForm,
    '[data-maintenance-detail-control="completed_on"]',
    item?.completed_on || "",
  );

  const costInput = detailsForm.querySelector('input[name="cost"]');
  if (costInput) {
    costInput.value = formatCurrency(item?.cost) || "";
  }

  if (stream === "issue") {
    const detailsRoot = detailsForm.closest("#details-view") || document;
    const repairDetailsContainer = getLongTextContainer(
      detailsRoot,
      "repair_details",
    );
    setLongTextContainerValue(
      repairDetailsContainer,
      item?.repair_details || "",
      () => {},
    );
  }

  getCompletionFieldNames(stream).forEach((fieldName) => {
    recordDetailFieldBaseline(detailsForm, fieldName);
    clearDetailDirtyField(fieldName);
  });
  state.hasUnsavedChanges = hasDetailDirtyFields(detailsForm);
}

function validateMaintenanceDetailsForm(detailsForm, item) {
  if (!detailsForm) {return false;}
  const candidate = getCompletionCandidate(item, getFormPayload(detailsForm));
  applyCompletionRequiredState(detailsForm, candidate);
  const missingRepairLabels = isCloseInterceptStatus(candidate.status, candidate.stream) ?
    getMissingRepairDynamicFieldLabels(candidate) :
    [];
  if (missingRepairLabels.length > 0) {
    setRepairDetailsInlineError(
      detailsForm,
      `${missingRepairLabels.join(", ")} ${missingRepairLabels.length === 1 ? "is" : "are"} required.`,
    );
  }

  const nativeValid = validateNativeFormFields(detailsForm);
  const registeredValidation = validateRegisteredControls(detailsForm);
  const repairFieldsValid = missingRepairLabels.length === 0;
  if (!nativeValid || !registeredValidation.valid || !repairFieldsValid) {
    if (nativeValid) {
      focusFirstRegisteredControlError(registeredValidation);
    }
    if (nativeValid && registeredValidation.valid && !repairFieldsValid) {
      focusRepairDetailsValidation(detailsForm);
    }
    return false;
  }
  return true;
}

async function saveDirtyMaintenanceDetailsBeforeClose({
  detailsForm,
  item,
}) {
  if (!validateMaintenanceDetailsForm(detailsForm, item)) {return false;}
  const payload = getFormPayload(detailsForm);
  const didSave = await persistMaintenancePayload({
    item,
    payload,
    showBlockerModal: false,
  });
  if (!didSave) {return false;}
  Object.assign(item, payload);
  state.hasUnsavedChanges = false;
  clearDetailDirtyFields();
  return true;
}

function openUnsavedCompletionFieldsModal({
  dirtyFields = [],
  onCancel,
  onContinue,
  onSave,
}) {
  const fieldList = dirtyFields
    .map((fieldName) => `<li>${sanitizeHTML(getCompletionFieldLabel(fieldName))}</li>`)
    .join("");
  openConfirmationModal(
    "Unsaved Completion Details",
    `
      <p class="mb-3 text-left">There are unsaved changes to completion details required before closing this Maintenance item.</p>
      <ul class="list-disc pl-5 text-left">${fieldList}</ul>
    `,
    async () => {
      const didSave = await onSave?.();
      return didSave !== false;
    },
    false,
    "Save",
    onCancel,
    "Saving...",
    "Cancel",
    {
      closeOnError: false,
      keepOpenOnFalse: true,
      leftAction: {
        buttonText: "Continue",
        danger: true,
        onClick: async () => {
          await onContinue?.();
        },
      },
    },
  );
}

function rollbackStatusControl({
  controlId,
  item,
  previousStatus,
  renderDetailsView,
  updateSelectBackgrounds,
}) {
  if (item) {
    item.status = previousStatus || "";
  }
  if (controlId) {
    updateControlValue(controlId, previousStatus || "", {silent: true});
    updateSelectBackgrounds?.();
    return;
  }
  renderDetailsView?.();
  updateSelectBackgrounds?.();
}

function getPreflightWithoutMaintenanceCompletion(preflight = {}) {
  const groups = (preflight.groups || [])
    .filter((group) => group?.id !== "maintenanceCompletion");
  return {
    ...preflight,
    groups,
    shouldIntercept: groups.length > 0,
  };
}

function applyLocalCloseResult(item, targetStatusId, completionData = {}) {
  if (!item) {return;}
  item.status = targetStatusId;
  item.isClosedResolved = true;
  if (Object.prototype.hasOwnProperty.call(completionData, "technician")) {
    item.technician = String(completionData.technician || "").trim();
  }
  if (Object.prototype.hasOwnProperty.call(completionData, "cost")) {
    item.cost = normalizeCurrencyInput(completionData.cost);
  }
  if (Object.prototype.hasOwnProperty.call(completionData, "completed_on")) {
    item.completed_on = completionData.completed_on || null;
  }
  if (Object.prototype.hasOwnProperty.call(completionData, "repair_details")) {
    item.repair_details = completionData.repair_details || null;
  }
  state.hasUnsavedChanges = false;
}

function applyLocalHoldResult(item, targetStatusId) {
  if (!item) {return;}
  item.status = targetStatusId;
  item.isClosedResolved = false;
  state.hasUnsavedChanges = false;
}

function resetCloseDirtyStateAfterRender(item) {
  const detailsForm = document.querySelector("#details-form");
  if (!detailsForm) {return;}

  syncCompletionFieldDraftState({detailsForm, item});
  clearLiveSaveDirtyState(detailsForm, "status");
}

async function applyMaintenanceClose({
  cascade = {},
  completionData = {},
  detailsForm,
  item,
  renderDetailsView,
  selections = {},
  targetStatusId,
  updateSelectBackgrounds,
}) {
  const response = await applyParentCascadingClosure({
    cascade,
    completionData,
    itemId: item.id,
    itemType: "maintenance",
    selections,
    targetStatusId,
  });
  if (response?.success === false) {
    throw new Error(getMaintenanceCloseFailureMessage(response));
  }
  applyLocalCloseResult(item, targetStatusId, completionData);
  syncCompletionFieldDraftState({detailsForm, item});
  renderDetailsView?.();
  resetCloseDirtyStateAfterRender(item);
  updateSelectBackgrounds?.();
  return response;
}

async function applyMaintenanceHold({
  cascade = {},
  detailsForm,
  item,
  renderDetailsView,
  selections = {},
  targetStatusId,
  updateSelectBackgrounds,
}) {
  const response = await applyParentCascadingHold({
    cascade,
    itemId: item.id,
    itemType: "maintenance",
    selections,
    targetStatusId,
  });
  if (response?.success === false) {
    throw new Error(getMaintenanceCloseFailureMessage(response));
  }
  applyLocalHoldResult(item, targetStatusId);
  renderDetailsView?.();
  const activeForm = document.querySelector("#details-form") || detailsForm;
  clearLiveSaveDirtyState(activeForm, "status");
  updateSelectBackgrounds?.();
  return response;
}

async function handleMaintenanceHoldStatusChange({
  controlId,
  detailsForm,
  item,
  previousStatus,
  renderDetailsView,
  targetStatusId,
  updateSelectBackgrounds,
}) {
  if (!isMaintenanceOnHoldStatus(targetStatusId)) {
    return false;
  }

  const rollback = () => rollbackStatusControl({
    controlId,
    item,
    previousStatus,
    renderDetailsView,
    updateSelectBackgrounds,
  });

  try {
    const preflight = await getParentHoldPreflight({
      itemId: item.id,
      itemType: "maintenance",
      targetStatusId,
    });
    const finishHold = async ({cascade = {}, selections = {}} = {}) => {
      try {
        return await applyMaintenanceHold({
          cascade,
          detailsForm,
          item,
          renderDetailsView,
          selections,
          targetStatusId,
          updateSelectBackgrounds,
        });
      } catch (error) {
        rollback();
        throw error;
      }
    };

    if (preflight?.shouldIntercept) {
      openParentClosePreflightModal({
        preflight,
        onCancel: rollback,
        onApply: finishHold,
        onSkip: finishHold,
      });
      return true;
    }

    await finishHold();
    return true;
  } catch (error) {
    rollback();
    openConfirmationModal(
      "Maintenance Not Updated",
      sanitizeHTML(error?.message || "Maintenance could not be put on hold."),
      async () => {},
      false,
      "OK",
    );
    return true;
  }
}

async function runMaintenanceCloseFlow({
  completionData = {},
  controlId,
  deferPreflightModal = false,
  detailsForm,
  item,
  previousStatus,
  renderDetailsView,
  targetStatusId,
  updateSelectBackgrounds,
}) {
  const rollback = () => rollbackStatusControl({
    controlId,
    item,
    previousStatus,
    renderDetailsView,
    updateSelectBackgrounds,
  });
  const preflight = await getParentClosePreflight({
    itemId: item.id,
    itemType: "maintenance",
    targetStatusId,
  });
  const cascadePreflight = getPreflightWithoutMaintenanceCompletion(preflight);

  const finishClose = async ({cascade = {}, selections = {}} = {}) => {
    try {
      return await applyMaintenanceClose({
        cascade,
        completionData,
        detailsForm,
        item,
        renderDetailsView,
        selections,
        targetStatusId,
        updateSelectBackgrounds,
      });
    } catch (error) {
      rollback();
      throw error;
    }
  };

  if (cascadePreflight.shouldIntercept) {
    const openPreflight = () => openParentClosePreflightModal({
      preflight: cascadePreflight,
      onCancel: rollback,
      onApply: finishClose,
      onSkip: finishClose,
    });
    if (deferPreflightModal) {
      window.setTimeout(openPreflight, 0);
    } else {
      openPreflight();
    }
    return true;
  }

  await finishClose();
  return true;
}

async function handleMaintenanceCloseStatusChange({
  controlId,
  detailsForm,
  item,
  previousStatus,
  renderDetailsView,
  targetStatusId,
  updateSelectBackgrounds,
}) {
  const stream = getMaintenanceStream(item);
  if (!isCloseInterceptStatus(targetStatusId, stream)) {
    return false;
  }

  const rollback = () => rollbackStatusControl({
    controlId,
    item,
    previousStatus,
    renderDetailsView,
    updateSelectBackgrounds,
  });
  const submitClose = async (completionData = {}, options = {}) => {
    try {
      return await runMaintenanceCloseFlow({
        completionData,
        controlId,
        deferPreflightModal: options.deferPreflightModal === true,
        detailsForm,
        item,
        previousStatus,
        renderDetailsView,
        targetStatusId,
        updateSelectBackgrounds,
      });
    } catch (error) {
      rollback();
      throw error;
    }
  };

  const startClose = async (options = {}) => {
    const activeCloseItem = {
      ...item,
      ...(options.useCurrentFormValues && detailsForm ?
        getCompletionCandidate(item, getFormPayload(detailsForm)) :
        {}),
      status: targetStatusId,
    };
    try {
      const completionPreflight = await getParentClosePreflight({
        itemId: item.id,
        itemType: "maintenance",
        targetStatusId,
      });
      const frequencyGroup = completionPreflight?.groups?.find((group) =>
        group.id === "maintenanceServiceFrequency",
      );
      if (frequencyGroup) {
        rollback();
        openConfirmationModal(
            "Service Frequency Required",
            sanitizeHTML(
                frequencyGroup.items?.[0]?.message ||
                "Set a Service Frequency on the linked Equipment before " +
                "completing this Service.",
            ),
            async () => {},
            false,
            "OK",
        );
        return true;
      }
    } catch (error) {
      rollback();
      openConfirmationModal(
          "Maintenance Not Closed",
          sanitizeHTML(
              error?.message ||
              "Maintenance completion could not be checked.",
          ),
          async () => {},
          false,
          "OK",
      );
      return true;
    }
    const needsCompletionModal = options.forceCompletionModal === true ||
      shouldOpenMaintenanceCloseModal({
        item: activeCloseItem,
        config: appData?.config || {},
      });
    if (needsCompletionModal) {
      openMaintenanceCloseModal({
        item: activeCloseItem,
        config: appData?.config || {},
        onCancel: rollback,
        onSubmit: (completionData) => submitClose(
          completionData,
          {deferPreflightModal: true},
        ),
      });
      return true;
    }

    try {
      await submitClose();
    } catch (error) {
      openConfirmationModal(
        "Maintenance Not Closed",
        sanitizeHTML(error?.message || "Maintenance could not be closed."),
        async () => {},
        false,
        "OK",
      );
    }
    return true;
  };

  const dirtyCompletionFields = getDirtyCompletionFields(detailsForm, stream);
  if (dirtyCompletionFields.length > 0) {
    rollback();
    openUnsavedCompletionFieldsModal({
      dirtyFields: dirtyCompletionFields,
      onCancel: rollback,
      onContinue: async () => {
        window.setTimeout(() => {
          void startClose({
            forceCompletionModal: true,
            useCurrentFormValues: true,
          });
        }, 0);
      },
      onSave: async () => {
        const didSave = await saveDirtyMaintenanceDetailsBeforeClose({
          detailsForm,
          item,
        });
        if (!didSave) {return false;}
        window.setTimeout(() => {
          void startClose();
        }, 0);
        return true;
      },
    });
    return true;
  }

  if (shouldOpenMaintenanceCloseModal({
    item: {
      ...item,
      status: targetStatusId,
    },
    config: appData?.config || {},
  })) {
    return startClose();
  }

  return startClose();
}

async function persistMaintenancePayload({
  item,
  showBlockerModal = true,
  payload,
  onFailure,
}) {
  const candidate = getCompletionCandidate(item, payload);
  const blockers = getCompletionBlockers(candidate);
  if (blockers.length > 0) {
    if (showBlockerModal) {
      showMaintenanceSaveBlockers(blockers);
    }
    onFailure?.();
    return false;
  }

  const result = await saveMaintenanceItem({
    itemId: item.id,
    data: payload,
  });

  if (result?.success === false) {
    const responseBlockers = (result.blockers || [])
      .map((blocker) => blocker?.message)
      .filter(Boolean);
    if (showBlockerModal) {
      showMaintenanceSaveBlockers(responseBlockers);
    }
    onFailure?.();
    return false;
  }

  return true;
}

function resetSaveButtonsIfNoManualDirtyFields(detailsForm) {
  if (!detailsForm || hasDetailDirtyFields(detailsForm)) {return;}
  state.hasUnsavedChanges = false;
  const root = detailsForm.closest("#details-view") || document;
  root.querySelectorAll(".details-save-btn").forEach((button) => {
    button.disabled = true;
    button.classList.remove(
      "bg-[var(--dfc-brand-primary)]",
      "hover:bg-[var(--dfc-brand-primary-hover)]",
      "shadow-md",
      "text-gray-900",
    );
    button.classList.add("bg-gray-200", "hover:bg-gray-300", "text-gray-800");
  });
}

function clearLiveSaveDirtyState(detailsForm, fieldName) {
  clearDetailDirtyField(fieldName);
  resetSaveButtonsIfNoManualDirtyFields(detailsForm);
}

async function saveMaintenanceLiveField({
  controlId,
  detailsForm,
  fieldName,
  item,
  renderDetailsView,
  updateSelectBackgrounds,
  value,
}) {
  const previousValue = item?.[fieldName];
  if (fieldName === "status") {
    const handledClose = await handleMaintenanceCloseStatusChange({
      controlId,
      detailsForm,
      item,
      previousStatus: previousValue,
      renderDetailsView,
      targetStatusId: value,
      updateSelectBackgrounds,
    });
    if (handledClose) {
      clearLiveSaveDirtyState(detailsForm, fieldName);
      return true;
    }
    const handledHold = await handleMaintenanceHoldStatusChange({
      controlId,
      detailsForm,
      item,
      previousStatus: previousValue,
      renderDetailsView,
      targetStatusId: value,
      updateSelectBackgrounds,
    });
    if (handledHold) {
      clearLiveSaveDirtyState(detailsForm, fieldName);
      return true;
    }
  }

  const payload = {
    [fieldName]: fieldName === "cost" ? normalizeCurrencyInput(value) : value,
  };

  try {
    const didSave = await persistMaintenancePayload({
      item,
      payload,
      onFailure: () => {
        if (controlId) {
          updateControlValue(controlId, previousValue, {silent: true});
        }
        renderDetailsView?.();
        updateSelectBackgrounds?.();
      },
    });
    if (didSave) {
      item[fieldName] = payload[fieldName];
      clearLiveSaveDirtyState(detailsForm, fieldName);
      updateSelectBackgrounds?.();
      return true;
    }
    return false;
  } catch (error) {
    console.error("Failed to save Maintenance detail field:", error);
    openConfirmationModal(
      "Error",
      error?.message || "Failed to save Maintenance detail field.",
      async () => {},
      false,
      "OK",
    );
    renderDetailsView?.();
    updateSelectBackgrounds?.();
    return false;
  }
}

/**
 * Saves the Maintenance detail form through backend Maintenance validation.
 * @param {object} params Save params.
 * @param {HTMLFormElement} params.detailsForm Detail form.
 * @param {object} params.item Maintenance item.
 * @returns {Promise<boolean>} True when save succeeds.
 */
export async function saveMaintenanceDetailsForm({detailsForm, item}) {
  if (!detailsForm || !item?.id) {return false;}
  if (!validateMaintenanceDetailsForm(detailsForm, item)) {return false;}
  const payload = getFormPayload(detailsForm);
  const didSave = await persistMaintenancePayload({
    item,
    payload,
    showBlockerModal: false,
  });
  if (didSave) {
    state.hasUnsavedChanges = false;
  }
  return didSave;
}

function getLinkFailureMessage(response) {
  const blockerMessages = (response?.blockers || [])
    .map((blocker) => blocker?.message)
    .filter(Boolean);
  if (blockerMessages.length > 0) {
    return blockerMessages.join(" ");
  }
  if (response?.reason === "stale_state") {
    return "This Maintenance item changed since the action was opened. Refresh and try again.";
  }
  if (response?.reason === "asset_closed") {
    return "Closed assets cannot be linked to Maintenance items.";
  }
  return "The linked Asset could not be updated.";
}

function applyAssetLinkResult(item, response) {
  if (!item || !response?.current) {return;}
  if (response.changed === false) {return;}
  item.linked_asset_id = response.current.assetId || null;
  if (typeof response.current.item === "string") {
    item.item = response.current.item;
  }
  item.linked_asset_name = response.current.assetName || "";
  item.linked_asset_serial_number = response.current.serial_number || "";
  item.linked_asset_types = Array.isArray(response.current.types) ?
    response.current.types :
    [];
}

async function updateDetailAssetLink({assetId, item}) {
  const response = await updateMaintenanceAssetLink({
    maintenanceId: item.id,
    assetId,
    expectedUpdatedAt: item.updatedAt || null,
  });
  if (response?.success === false) {
    throw new Error(getLinkFailureMessage(response));
  }
  applyAssetLinkResult(item, response);
  return response;
}

function attachMaintenanceAssetLinkListeners(detailsViewContainer, item, context) {
  attachMaintenanceAssetLinkFieldListeners(detailsViewContainer, {
    onLink: () => {
      openMaintenanceAssetLookupModal({
        selectedAssetId: item?.linked_asset_id || "",
        selectedAsset: getMaintenanceAssetDisplay(item),
        onSelect: async (asset) => {
          await updateDetailAssetLink({
            assetId: asset.id,
            item,
          });
          context.renderDetailsView?.();
          return true;
        },
      });
    },
    onOpen: ({targetId}) => {
      if (!targetId || !userCanOpenAssets()) {return;}
      context.showDetails?.(targetId, "asset");
    },
    onUnlink: () => {
      if (!item?.linked_asset_id) {return;}
      openConfirmationModal(
        "Clear Equipment Link",
        "Are you sure you want to unlink this item from its assigned equipment? You should only unlink the item if the linking is incorrect.",
        async () => {
          try {
            await updateDetailAssetLink({
              assetId: null,
              item,
            });
            context.renderDetailsView?.();
          } catch (error) {
            openConfirmationModal(
              "Equipment Link Not Updated",
              sanitizeHTML(error?.message || "The linked Equipment could not be cleared."),
              async () => {},
              false,
              "OK",
            );
          }
        },
        true,
        "Clear Link",
        null,
        "Clearing...",
      );
    },
  });
}

/**
 * Detail adapter for Maintenance parent items.
 */
export const maintenanceDetailAdapter = {
  itemType: "maintenance",
  viewName: "maintenance",
  statusConfigKey: "maintenanceStatus",
  typeConfigKey: "maintenanceTypes",
  liveSaveFields: LIVE_SAVE_FIELDS,
  showManualSavePendingState: true,
  sections: [
    "linkedTasks",
    "checklist",
    "notes",
    "attachments",
  ],
  renderFields: getMaintenanceDetailFields,
  mountControls(detailsViewContainer, item) {
    setupMaintenanceDetailControls(detailsViewContainer, item);
  },
  handleControlValueChange({
    event,
    item,
    renderDetailsView,
    updateSelectBackgrounds,
    value,
  }) {
    const fieldName = event.detail?.name || "";
    if (!LIVE_SAVE_FIELDS.includes(fieldName)) {return false;}
    const detailsForm = event.target?.closest?.("#details-form") || null;
    void saveMaintenanceLiveField({
      controlId: event.detail?.id || "",
      detailsForm,
      fieldName,
      item,
      renderDetailsView,
      updateSelectBackgrounds,
      value,
    });
    return true;
  },
  saveRegisteredDetailValue({
    controlId,
    detailsForm,
    fieldName,
    item,
    renderDetailsView,
    updateSelectBackgrounds,
    value,
  }) {
    return saveMaintenanceLiveField({
      controlId,
      detailsForm,
      fieldName,
      item,
      renderDetailsView,
      updateSelectBackgrounds,
      value,
    });
  },
  async saveManualDetails({detailsForm, item}) {
    return saveMaintenanceDetailsForm({detailsForm, item});
  },
  attachPostFormListeners(detailsViewContainer, item, context) {
    attachMaintenanceAssetLinkListeners(detailsViewContainer, item, context);
  },
  getBackNavigation() {
    return { type: "view", view: "maintenance" };
  },
  getDeleteNavigation() {
    return { type: "view", view: "maintenance" };
  },
};
